import React from "react";

// ─────────────────────────────────────────────────────────
// StandardReportCard — A4 portrait printable report card (Colorful)
// ─────────────────────────────────────────────────────────
// Props:
//   instituteInfo  : { logoURL, name }
//   studentName    : string
//   referenceId    : string (roll/student ID)
//   level          : string (e.g. "Grade 10")
//   periodLabel    : string (e.g. "2025 Term 1")
//   examinations   : [{ testType, subjects: [{ className, score, maxScore, percentage }], totalScore, totalMaxScore, totalPct }]
//   cumulativeScore     : number
//   cumulativeMaxScore  : number
//   cumulativePct       : number
//   rank           : number | null (optional)
//   totalStudents  : number | null (optional)
// ─────────────────────────────────────────────────────────

const getGrade = (pct, mapping) => {
  if (!mapping || mapping.length === 0) return { grade: "—", remark: "" };
  const sorted = [...mapping].sort((a, b) => b.minPercentage - a.minPercentage);
  const match = sorted.find((m) => pct >= m.minPercentage);
  return match ? { grade: match.grade, remark: match.remark } : { grade: "—", remark: "" };
};

const StandardReportCard = ({
  instituteInfo,
  studentName,
  referenceId,
  level,
  periodLabel,
  examinations = [],
  cumulativeScore,
  cumulativeMaxScore,
  cumulativePct,
  rank,
  totalStudents,
  gradeLetterMapping = [],
}) => {
  const logoUrl =
    instituteInfo?.logoURL ||
    instituteInfo?.logo ||
    instituteInfo?.instituteLogo ||
    "https://res.cloudinary.com/dfdsncmih/image/upload/v1761210947/grademe_ai_logo_crp_vrjuhf.png";
  const instituteName =
    instituteInfo?.name || instituteInfo?.instituteName || "GradeMe AI";
  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Build cumulative per-subject data across all examinations
  // maxScore is always counted (the exam had those marks available),
  // absent subjects contribute 0 to score.
  const cumulativeBySubject = {};
  examinations.forEach((exam) => {
    (exam.subjects || []).forEach((subj) => {
      const key = subj.className;
      if (!cumulativeBySubject[key]) {
        cumulativeBySubject[key] = { className: key, score: 0, maxScore: 0 };
      }
      cumulativeBySubject[key].maxScore += subj.maxScore || 0;
      if (!subj.absent) {
        cumulativeBySubject[key].score += subj.score || 0;
      }
    });
  });
  const cumulativeSubjects = Object.values(cumulativeBySubject).map((s) => ({
    ...s,
    percentage: s.maxScore > 0 ? (s.score / s.maxScore) * 100 : 0,
  }));

  const testTypeLabels = examinations.map((e) => e.testType).join(", ");

  return (
    <div className="rc-page">
      <div className="rc-main-container">
        {/* ── Decorative top band (Now inside centered container) ── */}
        <div className="rc-top-band">
          <div className="rc-band-stripe rc-stripe-1" />
          <div className="rc-band-stripe rc-stripe-2" />
          <div className="rc-band-stripe rc-stripe-3" />
        </div>

        {/* ── Corner decorative accents (Now relative to centered container) ── */}
        <div className="rc-corner-accent rc-corner-tl" />
        <div className="rc-corner-accent rc-corner-tr" />

        {/* ── Header: logo + institute ── */}
        <header className="rc-header">
          <div className="rc-logo-wrap">
            <img
              src={logoUrl}
              alt="Institute Logo"
              className="rc-logo"
              crossOrigin="anonymous"
              onError={(e) => {
                e.target.src =
                  "https://res.cloudinary.com/dfdsncmih/image/upload/v1759219779/grademe_ai_logo_sjzbso.png";
              }}
            />
          </div>
          <div className="rc-inst-info">
            <h1 className="rc-inst-name">{instituteName}</h1>
            <div className="rc-title-badge">
              <span>REPORT CARD</span>
            </div>
          </div>
        </header>

        {/* ── Student info strip ── */}
        <section className="rc-student-strip">
          <div className="rc-info-grid">
            <div className="rc-info-item">
              <span className="rc-info-label">Student Name</span>
              <span className="rc-info-value" style={{ overflowWrap: "anywhere" }}>{studentName || "—"}</span>
            </div>
            <div className="rc-info-item">
              <span className="rc-info-label">Student ID</span>
              <span className="rc-info-value" style={{ overflowWrap: "anywhere" }}>{referenceId || "—"}</span>
            </div>
            <div className="rc-info-item">
              <span className="rc-info-label">Level</span>
              <span className="rc-info-value">{level || "—"}</span>
            </div>
            <div className="rc-info-item">
              <span className="rc-info-label">Examinations</span>
              <span className="rc-info-value">{testTypeLabels || "—"}</span>
            </div>
            {periodLabel && (
              <div className="rc-info-item">
                <span className="rc-info-label">Academic Period</span>
                <span className="rc-info-value">{periodLabel}</span>
              </div>
            )}
            <div className="rc-info-item">
              <span className="rc-info-label">Date</span>
              <span className="rc-info-value">{dateStr}</span>
            </div>
          </div>
        </section>

        {/* ── Per-examination tables ── */}
        {examinations.map((exam, eIdx) => (
          <section className="rc-marks-section" key={eIdx}>
            <h2 className="rc-section-title">
              <span className="rc-section-icon">📝</span> {exam.testType}
            </h2>
            <table className="rc-table">
              <thead>
                <tr>
                  <th className="rc-th-sno">#</th>
                  <th className="rc-th-subject">Subject</th>
                  <th className="rc-th-marks">Marks Obtained</th>
                  <th className="rc-th-max">Max Marks</th>
                  {gradeLetterMapping.length > 0 && <th className="rc-th-grade">Grade</th>}
                  <th className="rc-th-pct">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {(exam.subjects || []).map((subj, idx) => {
                  const isAbsent = subj.absent === true;
                  const pct = subj.percentage ?? 0;
                  return (
                    <tr key={idx} className={isAbsent ? "rc-row-absent" : ""}>
                      <td className="rc-td-sno">{idx + 1}</td>
                      <td className="rc-td-subject">{subj.className}</td>
                      <td className="rc-td-marks">
                        {isAbsent
                          ? "AB"
                          : typeof subj.score === "number"
                            ? Math.round(subj.score * 10) / 10
                            : "—"}
                      </td>
                      <td className="rc-td-max">{subj.maxScore ?? "—"}</td>
                      {gradeLetterMapping.length > 0 && (
                        <td className="rc-td-grade">
                          {isAbsent ? "AB" : getGrade(pct, gradeLetterMapping).grade}
                        </td>
                      )}
                      <td className="rc-td-pct">
                        {isAbsent ? "AB" : `${Math.round(pct * 10) / 10}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="rc-total-row">
                  <td colSpan={2} className="rc-total-label">
                    TOTAL
                  </td>
                  <td className="rc-td-marks rc-total-val">
                    {typeof exam.totalScore === "number"
                      ? Math.round(exam.totalScore * 10) / 10
                      : "—"}
                  </td>
                  <td className="rc-td-max rc-total-val">
                    {exam.totalMaxScore ?? "—"}
                  </td>
                  {gradeLetterMapping.length > 0 && (
                    <td className="rc-td-grade rc-total-val">
                      {getGrade(exam.totalPct || 0, gradeLetterMapping).grade}
                    </td>
                  )}
                  <td className="rc-td-pct rc-total-val">
                    {Math.round((exam.totalPct || 0) * 10) / 10}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>
        ))}

        {/* ── Cumulative table (when multiple exams) ── */}
        {examinations.length > 1 && (
          <section className="rc-marks-section rc-cumulative-section">
            <h2 className="rc-section-title rc-cumulative-title">
              <span className="rc-section-icon">📊</span> Cumulative Performance
            </h2>
            <table className="rc-table rc-table-cumulative">
              <thead>
                <tr>
                  <th className="rc-th-sno">#</th>
                  <th className="rc-th-subject">Subject</th>
                  <th className="rc-th-marks">Total Marks</th>
                  <th className="rc-th-max">Max Marks</th>
                  {gradeLetterMapping.length > 0 && <th className="rc-th-grade">Grade</th>}
                  <th className="rc-th-pct">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {cumulativeSubjects.map((subj, idx) => {
                  const pct = subj.percentage ?? 0;
                  return (
                    <tr key={idx}>
                      <td className="rc-td-sno">{idx + 1}</td>
                      <td className="rc-td-subject">{subj.className}</td>
                      <td className="rc-td-marks">
                        {Math.round(subj.score * 10) / 10}
                      </td>
                      <td className="rc-td-max">{subj.maxScore}</td>
                      {gradeLetterMapping.length > 0 && (
                        <td className="rc-td-grade">{getGrade(pct, gradeLetterMapping).grade}</td>
                      )}
                      <td className="rc-td-pct">
                        {Math.round(pct * 10) / 10}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="rc-total-row">
                  <td colSpan={2} className="rc-total-label">
                    GRAND TOTAL
                  </td>
                  <td className="rc-td-marks rc-total-val">
                    {typeof cumulativeScore === "number"
                      ? Math.round(cumulativeScore * 10) / 10
                      : "—"}
                  </td>
                  <td className="rc-td-max rc-total-val">
                    {cumulativeMaxScore ?? "—"}
                  </td>
                  {gradeLetterMapping.length > 0 && (
                    <td className="rc-td-grade rc-total-val">
                      {getGrade(cumulativePct || 0, gradeLetterMapping).grade}
                    </td>
                  )}
                  <td className="rc-td-pct rc-total-val">
                    {Math.round((cumulativePct || 0) * 10) / 10}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>
        )}

        {/* ── Summary cards ── */}
        <section className="rc-summary">
          <div className="rc-summary-cards">
            <div className="rc-summary-card rc-sc-pct">
              <div className="rc-sc-icon">📊</div>
              <div className="rc-sc-value">
                {Math.round((cumulativePct || 0) * 10) / 10}%
              </div>
              <div className="rc-sc-label">Overall Percentage</div>
            </div>
            <div className="rc-summary-card rc-sc-marks">
              <div className="rc-sc-icon">✏️</div>
              <div className="rc-sc-value">
                {typeof cumulativeScore === "number"
                  ? Math.round(cumulativeScore * 10) / 10
                  : "—"}
                <span className="rc-rank-of"> / {cumulativeMaxScore ?? "—"}</span>
              </div>
              <div className="rc-sc-label">Total Marks</div>
            </div>
            {gradeLetterMapping.length > 0 && (
              <div className="rc-summary-card rc-sc-grade">
                <div className="rc-sc-icon">🌟</div>
                <div className="rc-sc-value">
                  {getGrade(cumulativePct || 0, gradeLetterMapping).grade}
                </div>
                <div className="rc-sc-label">Final Grade</div>
              </div>
            )}
            {gradeLetterMapping.length > 0 && getGrade(cumulativePct || 0, gradeLetterMapping).remark && (
              <div className="rc-summary-card rc-sc-remark">
                <div className="rc-sc-icon">💬</div>
                <div className="rc-sc-value" style={{fontSize: "1.3rem"}}>
                  {getGrade(cumulativePct || 0, gradeLetterMapping).remark}
                </div>
                <div className="rc-sc-label">Remark</div>
              </div>
            )}
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="rc-footer">
          <div className="rc-footer-left">
            <div className="rc-sign-line" />
            <span>Class Teacher</span>
          </div>
          <div className="rc-footer-center">
            <span className="rc-footer-watermark">Generated by GradeMe AI</span>
          </div>
          <div className="rc-footer-right">
            <div className="rc-sign-line" />
            <span>Principal</span>
          </div>
        </footer>

        {/* ── Bottom band (Now inside centered container) ── */}
        <div className="rc-bottom-band">
          <div className="rc-band-stripe rc-stripe-3" />
          <div className="rc-band-stripe rc-stripe-2" />
          <div className="rc-band-stripe rc-stripe-1" />
        </div>
      </div>
    </div>
  );
};

export default StandardReportCard;
